import { Icon, IconSizeClasses, Tooltip } from '@eppendorf/vnls-react-components';
import React from 'react';
import { Trans } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';

import { TypingChat } from '$components/chat-bot/typing-chat';

import { ChatMessage } from './types';

const markdownComponents = {
  a: ({ href, children }: React.ComponentPropsWithoutRef<'a'>) => (
    <a href={href} target="_blank" rel="noreferrer" className="chat-message-link">
      {children}
    </a>
  ),
};

interface MessageListProps {
  messages: ChatMessage[];
  isLoading?: boolean;
  onFeedback?: (messageId: string, feedback: 'up' | 'down') => void;
}

export function MessageList({
  messages,
  isLoading = false,
  onFeedback,
}: MessageListProps) {
  return (
    <div className="flex flex__dir--column gap-m">
      {messages.map((message) => (
        <div
          key={message.id}
          className={`flex flex__dir--column ${message.type === 'user' ? 'flex__ai--flex-end' : 'flex__ai--flex-start'}`}
        >
          <div
            className={`chat-message-bubble ${
              message.type === 'user'
                ? 'bg-blue-700 color-white chat-message-bubble--user'
                : 'bg-gray-50 chat-message-bubble--assistant'
            }`}
          >
            {message.type === 'assistant' ? (
              <div className="flex gap-s">
                <Icon name="chatbot_unmasked" size={IconSizeClasses.XSmall} />
                <div>
                  <div className="font-size-m chat-message-content">
                    <ReactMarkdown components={markdownComponents}>
                      {message.text}
                    </ReactMarkdown>
                  </div>
                  {message.isInitial && (
                    <div className="m-top-l">
                      <Trans
                        i18nKey="chatbot.initialMessageFurtherHelp"
                        components={{
                          l: (
                            <Link
                              target="_blank"
                              to="https://eppendorf.atlassian.net/servicedesk/customer/portals"
                              className="chat-message-link"
                            />
                          ),
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="font-size-m">
                <ReactMarkdown>{message.text}</ReactMarkdown>
              </div>
            )}
          </div>
          <div
            className={`flex flex__ai--center gap-xs m-top-xs ${message.type === 'user' ? 'flex__jc--flex-end' : 'flex__jc--flex-start'}`}
          >
            <span className="font-size-xs color-gray-900">
              {message.timestamp.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            {message.type === 'assistant' && !message.isInitial && onFeedback && (
              <div className="flex gap-xs">
                <Tooltip
                  trigger={
                    <button
                      type="button"
                      className="chat-feedback-btn"
                      aria-label="Like response"
                      aria-pressed={message.feedback === 'up'}
                      onClick={() => onFeedback(message.id, 'up')}
                    >
                      <Icon
                        name="chat-thumbs-up"
                        size={IconSizeClasses.XSmall}
                        className={
                          message.feedback === 'up' ? 'chat-icon--blue' : undefined
                        }
                      />
                    </button>
                  }
                >
                  Like response
                </Tooltip>
                <Tooltip
                  trigger={
                    <button
                      type="button"
                      className="chat-feedback-btn"
                      aria-label="Dislike response"
                      aria-pressed={message.feedback === 'down'}
                      onClick={() => onFeedback(message.id, 'down')}
                    >
                      <Icon
                        name="chat-thumbs-down"
                        size={IconSizeClasses.XSmall}
                        className={
                          message.feedback === 'down' ? 'chat-icon--blue' : undefined
                        }
                      />
                    </button>
                  }
                >
                  Dislike response
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      ))}
      {isLoading && (
        <div className="flex flex__jc--flex-start">
          <div className="bg-gray-50 p-s flex flex__ai--center chat-loading-bubble">
            <div className="flex gap-xs w-full flex__ai--center">
              <TypingChat />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
